import { NEW_WEAPON_POOL, PASSIVE_POOL } from './levelUpPool.js';
import { FUSION_DEFS } from './fusions.js';

/**
 * The Spoils chest (BULLET-HEAVEN.md §H3) — what a wave stage pays out when
 * it clears.
 *
 * Before this, `wave:cleared` paid a silent percentage heal, which meant the
 * flattest moment in a run was the one place it should have been loudest: the
 * beat between surviving a stage and a boss walking on. Machine #3 in the doc
 * (variable-ratio reward on a slot-machine cadence) was the one Survivor
 * already had all the parts for and none of the presentation — `rollRarity`,
 * `weightedSample` and `luckyOdds` are a genuine slot machine that was being
 * rendered as three static cards.
 *
 * Ethical line, stated because it's the one that matters here: the
 * slot-machine *presentation* on an in-run reward is fine. The moment a chest
 * becomes a purchase it is a different and worse game. Nothing in this file
 * costs anything, and nothing here is ever sold.
 */

/**
 * Roll counts for the run's first three chests, in order — hardcoded, ignoring
 * CHEST_ROLL_WEIGHTS entirely.
 *
 * BULLET-HEAVEN.md §H3 specifies 3-1-5, citing Vampire Survivors. This is
 * 3-3-5, and the missing dip is a deliberate departure. The reasoning, because
 * a number that disagrees with the plan it came from should say why:
 *
 * The generous opening and the dip do two different jobs.
 *
 * The opening does something honest. A player whose first chest pays a single
 * reward can reasonably decide chests aren't worth the pause and start skipping
 * them, losing the whole feature to one bad sample. Opening generous prevents
 * that false negative. It is onboarding, and it communicates something true:
 * chests matter, stop and read them.
 *
 * The dip does not. Chest 2 paying 1 teaches nothing — its function is
 * contrast, making chest 3's five land harder than it would after two threes.
 * What it actually installs is a belief that chests vary wildly and are
 * sometimes enormous, which is the variable-ratio schedule that sustains
 * compulsive opening. That is engagement engineering aimed at the player
 * rather than information offered to them, and unlike the generous opening it
 * buys the player nothing.
 *
 * So: keep the teaching, drop the conditioning. Nothing else about the chest
 * changes — the slot-machine presentation on an in-run reward is fine, and the
 * line the plan draws (never a purchase, never real money) holds either way.
 */
export const OPENING_CHEST_ROLLS = [3, 3, 5];

/**
 * Odds for every chest after the opening three, as [rolls, weight] pairs.
 * Expected value is 1.56 rolls, against the opening's 3.67 — the opening is
 * still, deliberately, better than the game that follows it. Raising these
 * would narrow that gap if it ever reads as a bait-and-switch; the gap itself
 * is what makes the late-run 5 an event.
 */
export const CHEST_ROLL_WEIGHTS = [
  [1, 62],
  [2, 26],
  [3, 9],
  [5, 3], // the jackpot the opening promised; rare enough to stay one
];

/** Chest quality by stage index — later gauntlet chests are events. */
export function chestTier(stageIndex) {
  if (stageIndex >= 6) return 'radiant';
  if (stageIndex >= 3) return 'gilded';
  return 'plain';
}

const TIER_RARITY_FLOOR = { plain: null, gilded: 'uncommon', radiant: 'rare' };

/**
 * Builds the reward pool for one chest against the run's live state.
 *
 * Every entry is `{ id, label, detail, rarity, apply }` — the same shape a
 * level-up card uses, so SpoilsChest.js renders with the existing card CSS and
 * nothing here needs its own styling vocabulary.
 *
 * @param {object} ctx
 * @param {import('../systems/combat/CombatSystem.js').CombatSystem} ctx.combat
 * @param {object} ctx.player
 * @param {import('../systems/progression/ProgressionSystem.js').ProgressionSystem} ctx.progression
 * @param {string} ctx.tier - see chestTier
 */
export function buildSpoilsPool({ combat, player, progression, tier = 'plain' }) {
  const floor = TIER_RARITY_FLOOR[tier] ?? null;
  const pool = [];

  for (const weapon of combat.weapons) {
    const maxLevel = weapon.config?.maxLevel ?? 8;
    if (weapon.level >= maxLevel) continue;
    pool.push({
      id: `spoils:upgrade:${weapon.name}`,
      label: `${weapon.name} +1`,
      detail: `Level ${weapon.level} → ${weapon.level + 1}`,
      rarity: 'common',
      apply: () => weapon.levelUp(1),
    });
  }

  for (const def of NEW_WEAPON_POOL) {
    if (combat.weapons.some((w) => w.name === def.name)) continue;
    if (combat.consumedByFusion?.has(def.name)) continue; // never hand back a spent fusion parent
    pool.push({
      id: `spoils:new:${def.name}`,
      label: def.name,
      detail: def.description,
      rarity: 'uncommon',
      apply: () => combat.addWeapon(def.create()),
    });
  }

  for (const passive of PASSIVE_POOL) {
    if (progression._ownedNonStackablePassives.has(passive.id)) continue;
    pool.push({
      id: `spoils:passive:${passive.id}`,
      label: passive.label,
      detail: passive.description,
      rarity: passive.rarity,
      apply: () => {
        passive.apply(player, combat);
        progression._ownedNonStackablePassives.add(passive.id);
        progression._passivePicks.set(passive.label, (progression._passivePicks.get(passive.label) ?? 0) + 1);
      },
    });
  }

  // The heal the chest replaced, kept as one entry among many rather than
  // deleted — a chest that can't pay HP is a chest you resent opening at low
  // health, and this is the stage boundary right before a boss.
  pool.push({
    id: 'spoils:heal',
    label: 'Field Dressing',
    detail: 'Restore 25% of max HP.',
    rarity: 'common',
    apply: () => { player.hp = Math.min(player.maxHp, player.hp + player.maxHp * 0.25); },
  });

  // Fusion-progress push: levels a Harmonics parent straight to the gate. The
  // rare one, and the reason a chest can change the shape of a run rather than
  // just its numbers — it can be the thing that finally puts a recipe in reach
  // (see BULLET-HEAVEN.md §H4, and the recipe panel that lets the player know
  // this is what they wanted).
  for (const def of FUSION_DEFS) {
    if (combat.weapons.some((w) => w.name === def.name)) continue;
    for (const parentName of def.parents) {
      const weapon = combat.weapons.find((w) => w.name === parentName);
      if (!weapon) continue;
      const required = progression._fusionParentRequirement(parentName);
      if (weapon.level >= required) continue;
      pool.push({
        id: `spoils:harmonic:${def.id}:${parentName}`,
        label: `Attunement: ${parentName}`,
        detail: `${parentName} → level ${required}, toward ${def.name}.`,
        rarity: 'rare',
        apply: () => weapon.levelUp(required - weapon.level),
      });
    }
  }

  if (!floor) return pool;
  // A tier floor filters rather than upgrades: a gilded chest that "promotes"
  // a common to uncommon would be a relabelled common. If the floor empties
  // the pool (everything maxed), fall back to the unfiltered one — a chest
  // that opens to nothing is worse than a chest that opens to a heal.
  const filtered = pool.filter((entry) => rarityAtLeast(entry.rarity, floor));
  return filtered.length > 0 ? filtered : pool;
}

const RARITY_ORDER = ['common', 'uncommon', 'rare'];

function rarityAtLeast(rarity, floor) {
  return RARITY_ORDER.indexOf(rarity) >= RARITY_ORDER.indexOf(floor);
}

/**
 * How many rewards this chest pays. `chestIndex` is 0-based across the run,
 * so the opening three are the hardcoded ones.
 */
export function rollChestSize(chestIndex, random = Math.random) {
  if (chestIndex < OPENING_CHEST_ROLLS.length) return OPENING_CHEST_ROLLS[chestIndex];
  const total = CHEST_ROLL_WEIGHTS.reduce((sum, [, weight]) => sum + weight, 0);
  let roll = random() * total;
  for (const [rolls, weight] of CHEST_ROLL_WEIGHTS) {
    roll -= weight;
    if (roll <= 0) return rolls;
  }
  return CHEST_ROLL_WEIGHTS[0][0];
}
